import React, { Fragment, useEffect, useState } from 'react'

import { CssBaseline } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { CookiesProvider } from 'react-cookie'
import { Provider } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ReactComponent as ModuleLogo } from 'assets/images/AuthModuleLogo.svg'
import OopsMinion from 'assets/images/minions/OopsMinion.webp'
import App from 'containers/app/App'
import store from 'redux/store'
import theme from 'theme'
import { getClassNameForLanguage } from 'utils/common'
import { shouldShowBuilderNotice } from 'utils/isMobileDevice'

import './MobileBuilderNotice.scss'

const getHomeUrl = (language: string): string => {
    const promoUrl = (
        process.env.REACT_APP_PROMO_REDIRECT_URL || 'https://yourmodule.com'
    ).replace(/\/$/, '')

    return language === 'uk' ? `${promoUrl}/ua` : `${promoUrl}/`
}

const MobileBuilderNotice = (): React.ReactElement => {
    const { t, i18n } = useTranslation(['mobileBuilder'])
    const titleLines = t('mobileBuilder:title').split('\n')
    const titleFont = getClassNameForLanguage({
        i18n,
        uk: 'uk-extrabold',
        eng: 'eng-extrabold',
    })
    const descriptionFont = getClassNameForLanguage({
        i18n,
        uk: 'uk-regular',
        eng: 'eng-regular',
    })

    return (
        <main className='mobile-builder'>
            <ModuleLogo className='mobile-builder__logo' aria-label='MODULE' />
            <img className='mobile-builder__mascot' src={OopsMinion} alt='' />
            <h1 className={`mobile-builder__title ${titleFont}`}>
                {titleLines.map((line, index) => (
                    <Fragment key={line}>
                        {index > 0 && <br />}
                        {line}
                    </Fragment>
                ))}
            </h1>
            <p className={`mobile-builder__description ${descriptionFont}`}>
                {t('mobileBuilder:description')}
            </p>
            <a
                className={`mobile-builder__home ${titleFont}`}
                href={getHomeUrl(i18n.language)}
            >
                {t('mobileBuilder:backHome')}
            </a>
        </main>
    )
}

export const BuilderGate = (): React.ReactElement => {
    const [showNotice, setShowNotice] = useState(() =>
        shouldShowBuilderNotice(window.innerWidth),
    )

    useEffect(() => {
        const update = () => {
            setShowNotice(shouldShowBuilderNotice(window.innerWidth))
        }

        window.addEventListener('resize', update)
        window.addEventListener('orientationchange', update)

        return () => {
            window.removeEventListener('resize', update)
            window.removeEventListener('orientationchange', update)
        }
    }, [])

    if (showNotice) {
        return <MobileBuilderNotice />
    }

    return (
        <CookiesProvider>
            <Provider store={store}>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <App />
                </ThemeProvider>
            </Provider>
        </CookiesProvider>
    )
}

export default MobileBuilderNotice
